import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import { PageShell, Breadcrumbs } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { formatDate } from "@/data/posts";
import { usePosts } from "@/hooks/useContent";
import { useSeo } from "@/hooks/useSeo";

const Block = ({ b }) => {
  if (b.h) return <h2 className="font-serif font-semibold text-[#103080] text-2xl sm:text-3xl leading-tight mt-12 mb-4">{b.h}</h2>;
  if (b.ul) return (
    <ul className="my-5 space-y-2.5 pl-1">
      {b.ul.map((it) => (
        <li key={it} className="flex gap-3 text-[#0F172A]/75 leading-relaxed">
          <span className="mt-[11px] w-1.5 h-1.5 rounded-full bg-[#0090D0] shrink-0" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
  if (b.note) return (
    <aside className="my-8 rounded-2xl bg-[#103080] text-white p-6 sm:p-7 leading-relaxed">{b.note}</aside>
  );
  return <p className="my-5 text-[#0F172A]/75 leading-relaxed text-base sm:text-lg">{b.p}</p>;
};

export default function BlogPostPage() {
  const { slug } = useParams();
  const POSTS = usePosts();
  const post = POSTS.find((p) => p.slug === slug);

  useSeo({
    title: post ? `${post.title} | Rosevale Accountants, Bristol & Bath` : "Blog | Rosevale Accountants, Bristol & Bath",
    description: post ? post.excerpt : "",
    path: `/blog/${slug}`,
  });

  if (!post) return POSTS.loaded ? <Navigate to="/blog" replace /> : null;

  const sorted = [...POSTS].sort((a, b) => b.date.localeCompare(a.date));
  const pos = sorted.findIndex((p) => p.slug === slug);
  const newer = sorted[pos - 1];
  const older = sorted[pos + 1];

  return (
    <PageShell testid={`blog-post-page`}>
      <article>
        <header className="bg-[#F5F2EB] border-b border-[#103080]/10 py-16 sm:py-24">
          <div className="max-w-3xl mx-auto px-5 sm:px-8">
            <Breadcrumbs items={[{ label: "Blog", to: "/blog" }, { label: post.title.length > 40 ? post.title.slice(0, 40) + "…" : post.title }]} />
            <div className="flex flex-wrap gap-2 mb-6">
              {post.tags.map((t) => (
                <span key={t} className="rounded-full bg-white border border-[#103080]/10 px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-[#0090D0] font-medium">{t}</span>
              ))}
            </div>
            <h1 data-testid="page-title" className="font-serif font-semibold tracking-tight text-[#103080] text-3xl sm:text-5xl leading-[1.08]">{post.title}</h1>
            <p className="mt-6 text-sm text-[#0F172A]/55">
              By <span className="text-[#103080] font-medium">{post.author}</span> · {formatDate(post.date)} · {post.readTime}
            </p>
          </div>
        </header>

        <div className="max-w-5xl mx-auto px-5 sm:px-8 -mt-10 sm:-mt-14">
          <Reveal>
            <img src={post.image} alt="" className="w-full h-64 sm:h-[420px] object-cover rounded-3xl lift-shadow" />
          </Reveal>
        </div>

        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-14 sm:py-20" data-testid="post-body">
          {post.content.map((b, i) => <Block key={i} b={b} />)}

          <div className="mt-14 rounded-3xl bg-[#F5F2EB] border border-[#103080]/10 p-7 sm:p-9">
            <p className="text-xs uppercase tracking-[0.3em] text-[#0090D0] font-medium mb-3">Want this applied to your numbers?</p>
            <h3 className="font-serif font-semibold text-2xl text-[#103080]">Book a free discovery call.</h3>
            <p className="mt-2 text-sm text-[#0F172A]/65">Whether you're an existing client or thinking about working with us, we'd love to help. Or call us on 0117 321 0360.</p>
            <a href="https://calendly.com/abbie-browne" target="_blank" rel="noreferrer" data-testid="post-book-call-button" className="group mt-6 inline-flex items-center gap-2 rounded-full bg-[#103080] text-white text-sm font-medium px-6 py-3 hover:bg-[#0090D0] transition-colors duration-300">
              Book now <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>
        </div>
      </article>

      <nav className="max-w-5xl mx-auto px-5 sm:px-8 pb-20 grid grid-cols-1 sm:grid-cols-2 gap-5" aria-label="More posts">
        {older ? (
          <Link to={`/blog/${older.slug}`} data-testid="post-nav-older" className="group rounded-2xl bg-white border border-[#103080]/10 p-6 lift-shadow">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#0F172A]/45"><ArrowLeft size={13} /> Older</span>
            <p className="font-serif font-semibold text-[#103080] mt-2 leading-snug group-hover:text-[#0090D0] transition-colors duration-300">{older.title}</p>
          </Link>
        ) : <span />}
        {newer && (
          <Link to={`/blog/${newer.slug}`} data-testid="post-nav-newer" className="group rounded-2xl bg-white border border-[#103080]/10 p-6 lift-shadow sm:text-right">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#0F172A]/45">Newer <ArrowRight size={13} /></span>
            <p className="font-serif font-semibold text-[#103080] mt-2 leading-snug group-hover:text-[#0090D0] transition-colors duration-300">{newer.title}</p>
          </Link>
        )}
      </nav>
    </PageShell>
  );
}
